import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSites } from '../hooks/useSites';
import { useBatiments } from '../hooks/useBatiments';
import { usePieces } from '../hooks/usePieces';
import { useZones } from '../hooks/useZones';
import { useBoxes } from '../hooks/useBoxes';

interface LancerInventaireModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LancerInventaireModal: React.FC<LancerInventaireModalProps> = ({ isOpen, onClose }) => {
  const [selectedScope, setSelectedScope] = useState<'all' | 'batiment' | 'piece' | 'zone' | 'box'>('all');
  const [selectedBatiment, setSelectedBatiment] = useState('');
  const [selectedPiece, setSelectedPiece] = useState('');
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [selectedBoxes, setSelectedBoxes] = useState<string[]>([]);
  const navigate = useNavigate();

  const { data: batiments = [], isLoading: batimentsLoading } = useBatiments();
  const { data: pieces = [], isLoading: piecesLoading } = usePieces();
  const { data: zones = [], isLoading: zonesLoading } = useZones();
  const { data: boxes = [], isLoading: boxesLoading } = useBoxes();

  if (!isOpen) return null;

  // Filtrage hiérarchique
  const filteredPieces = selectedBatiment
    ? pieces.filter((p: any) => p.batimentId === selectedBatiment)
    : pieces;

  const filteredZones = selectedPiece
    ? zones.filter((z: any) => z.pieceId === selectedPiece)
    : zones;

  const handleStart = () => {
    // Créer une session d'inventaire et naviguer vers la page d'inventaire
    const inventorySession = {
      id: `inv-${Date.now()}`,
      scope: selectedScope,
      batimentId: selectedScope === 'batiment' ? selectedBatiment : undefined,
      pieceId: selectedScope === 'piece' ? selectedPiece : undefined,
      zoneIds: selectedScope === 'zone' ? selectedZones : undefined,
      boxIds: selectedScope === 'box' ? selectedBoxes : undefined,
      startedAt: new Date().toISOString(),
      startedBy: localStorage.getItem('userId') || 'unknown',
    };
    
    // Stocker la session dans localStorage
    localStorage.setItem('currentInventorySession', JSON.stringify(inventorySession));
    
    // Naviguer vers l'inventaire en mode contrôle
    navigate('/inventaire', { state: { inventoryMode: true, session: inventorySession } });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                📋 Lancer un Inventaire
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  ℹ️ Mode Inventaire
                </h4>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li>Parcourez vos objets et marquez-les comme inventoriés</li>
                  <li>Plusieurs personnes peuvent travailler simultanément</li>
                  <li>Chaque objet est horodaté avec votre nom</li>
                  <li>Vous pouvez faire l'inventaire en plusieurs fois</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Périmètre de l'inventaire
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="all"
                      checked={selectedScope === 'all'}
                      onChange={(e) => setSelectedScope(e.target.value as any)}
                      className="form-radio text-blue-600"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      🏢 Tous les objets de l'association
                    </span>
                  </label>
                  
                  <label className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="batiment"
                      checked={selectedScope === 'batiment'}
                      onChange={(e) => setSelectedScope(e.target.value as any)}
                      className="form-radio text-blue-600"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      🏗️ Un bâtiment spécifique
                    </span>
                  </label>
                  
                  <label className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="piece"
                      checked={selectedScope === 'piece'}
                      onChange={(e) => setSelectedScope(e.target.value as any)}
                      className="form-radio text-blue-600"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      🚪 Une ou des pièces spécifiques
                    </span>
                  </label>
                  
                  <label className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="zone"
                      checked={selectedScope === 'zone'}
                      onChange={(e) => setSelectedScope(e.target.value as any)}
                      className="form-radio text-blue-600"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      🏠 Une ou des zones spécifiques
                    </span>
                  </label>
                  
                  <label className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="box"
                      checked={selectedScope === 'box'}
                      onChange={(e) => setSelectedScope(e.target.value as any)}
                      className="form-radio text-blue-600"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      📦 Une ou des boxes mobiles
                    </span>
                  </label>
                </div>
              </div>

              {/* Sélection Bâtiment */}
              {selectedScope === 'batiment' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bâtiment à inventorier
                  </label>
                  <select
                    value={selectedBatiment}
                    onChange={(e) => setSelectedBatiment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner un bâtiment</option>
                    {batiments.map((batiment: any) => (
                      <option key={batiment.id} value={batiment.id}>
                        {batiment.nom} ({batiment.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Sélection Pièce */}
              {selectedScope === 'piece' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filtrer par bâtiment (optionnel)
                  </label>
                  <select
                    value={selectedBatiment}
                    onChange={(e) => setSelectedBatiment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tous les bâtiments</option>
                    {batiments.map((batiment: any) => (
                      <option key={batiment.id} value={batiment.id}>
                        {batiment.nom}
                      </option>
                    ))}
                  </select>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pièce(s) à inventorier
                  </label>
                  <select
                    value={selectedPiece}
                    onChange={(e) => setSelectedPiece(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner une pièce</option>
                    {filteredPieces.map((piece: any) => (
                      <option key={piece.id} value={piece.id}>
                        {piece.nom} ({piece.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Sélection Zone(s) */}
              {selectedScope === 'zone' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filtrer par pièce (optionnel)
                  </label>
                  <select
                    value={selectedPiece}
                    onChange={(e) => setSelectedPiece(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Toutes les pièces</option>
                    {pieces.map((piece: any) => (
                      <option key={piece.id} value={piece.id}>
                        {piece.nom}
                      </option>
                    ))}
                  </select>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zone(s) à inventorier
                  </label>
                  <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                    {zonesLoading ? (
                      <div className="p-4 text-center text-gray-500">Chargement...</div>
                    ) : filteredZones.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">Aucune zone disponible</div>
                    ) : (
                      filteredZones.map((zone: any) => (
                        <label
                          key={zone.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={zone.id}
                            checked={selectedZones.includes(zone.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedZones([...selectedZones, zone.id]);
                              } else {
                                setSelectedZones(selectedZones.filter(id => id !== zone.id));
                              }
                            }}
                            className="form-checkbox text-blue-600"
                          />
                          <span className="text-sm">
                            <span className="font-medium">{zone.code}</span> - {zone.nom}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Sélection Box(es) */}
              {selectedScope === 'box' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Box(es) à inventorier
                  </label>
                  <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                    {boxesLoading ? (
                      <div className="p-4 text-center text-gray-500">Chargement...</div>
                    ) : boxes.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">Aucune box disponible</div>
                    ) : (
                      boxes.map((box: any) => (
                        <label
                          key={box.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={box.id}
                            checked={selectedBoxes.includes(box.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedBoxes([...selectedBoxes, box.id]);
                              } else {
                                setSelectedBoxes(selectedBoxes.filter(id => id !== box.id));
                              }
                            }}
                            className="form-checkbox text-blue-600"
                          />
                          <span className="text-sm">
                            <span className="font-medium">{box.code}</span> - {box.nom}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleStart}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Démarrer l'inventaire
            </button>
            <button
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LancerInventaireModal;

